import isEqual from 'lodash/isEqual';
import {
  type Dispatch,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useEditMode } from '../components/EditModeProvider/EditModeProvider';
import {
  PageConfigProviderFromValue,
  type PageContextType,
  TabContentProviderFromValue,
} from '../components/PageConfigProvider/PageConfigProvider';
import type {
  PageAction,
  TabContentAction,
} from '../components/PageConfigProvider/page-reducer';
import { PageType } from '../components/PageConfigProvider/types';
import { useReducerWithHistory } from '../components/PageConfigProvider/useReducerWithHistory';
import { buildReportFlowDocument } from '../components/ReportFlow/convert-grid-report-to-flow';
import { pageItems } from '../components/ReportFlow/report-flow-reducer';
import { useToast } from '../hooks/useToast';
import { reconcileReportDocument } from './reconcile-report-document';
import { ReportDocumentContext } from './report-document-context';
import {
  pageFromReportDocument,
  reportDocumentFromPage,
  reportTabContentView,
} from './report-document-converters';
import {
  isDocumentTabContentAction,
  type ReportDocumentAction,
  reportDocumentReducer,
} from './report-document-reducer';
import type { ReportDocumentConfig } from './report-document-types';
import { useReportDocumentMutations } from './use-report-documents';

type ChildrenProps = {
  tabContentId: string;
};

type ReportDocumentProviderProps = {
  initialConfig: ReportDocumentConfig;
  /** Already in the report-documents collection (PUT); otherwise the first save POSTs with the same id. */
  isPersisted: boolean;
  isUnsavedDraft?: boolean;
  children: (props: ChildrenProps) => ReactNode;
};

export function ReportDocumentProvider({
  children,
  initialConfig,
  isPersisted,
  isUnsavedDraft = false,
}: ReportDocumentProviderProps) {
  const [originalConfig, setOriginalConfig] =
    useState<ReportDocumentConfig>(initialConfig);
  const [hasBeenSaved, setHasBeenSaved] = useState(false);
  const [persisted, setPersisted] = useState(isPersisted);

  const {
    state: draftConfig,
    dispatch: dispatchDraft,
    undo,
    redo,
    canUndo,
    canRedo,
    reset: resetDraft,
  } = useReducerWithHistory<ReportDocumentConfig, ReportDocumentAction>(
    reportDocumentReducer,
    initialConfig
  );

  const [savedDraft, setSavedDraft] =
    useState<ReportDocumentConfig>(initialConfig);

  const { editMode } = useEditMode();
  const [previousEditMode, setPreviousEditMode] = useState(editMode);
  const [adoptedInitialConfig, setAdoptedInitialConfig] =
    useState(initialConfig);
  const modeChanged = previousEditMode !== editMode;
  const initialConfigChanged = adoptedInitialConfig !== initialConfig;
  if (modeChanged || (initialConfigChanged && !editMode)) {
    let latestConfig = originalConfig;
    if (initialConfigChanged) {
      setAdoptedInitialConfig(initialConfig);
      if (!isEqual(adoptedInitialConfig, initialConfig)) {
        latestConfig = initialConfig;
        setOriginalConfig(initialConfig);
        setPersisted(isPersisted);
      }
    }
    if (modeChanged) setPreviousEditMode(editMode);
    if (modeChanged || latestConfig !== originalConfig) {
      resetDraft(latestConfig);
      setSavedDraft(latestConfig);
    }
  }
  const { createReportDocument, updateReportDocument } =
    useReportDocumentMutations();
  const toast = useToast();

  const dispatchDocument: Dispatch<ReportDocumentAction> = useCallback(
    (action) => {
      if (editMode) {
        dispatchDraft(action);
      } else {
        setOriginalConfig((prev) => reportDocumentReducer(prev, action));
      }
    },
    [editMode, dispatchDraft]
  );

  const dispatch: Dispatch<PageAction> = useCallback(
    (action) => {
      switch (action.type) {
        case 'APPLY_TAB_CONTENT_ACTION': {
          const tabAction = action.payload.action;
          if (!isDocumentTabContentAction(tabAction)) break;
          const replace = (
            action as { replacePreviousHistorySnapshot?: boolean }
          ).replacePreviousHistorySnapshot;
          dispatchDocument({
            type: 'TAB_CONTENT',
            action: tabAction,
            ...(replace ? { replacePreviousHistorySnapshot: true } : {}),
          });
          break;
        }
        case 'UPDATE_PAGE_TITLE': {
          dispatchDocument({ type: 'UPDATE_NAME', payload: action.payload });
          break;
        }
        case 'SET_CONFIG': {
          if (action.payload.type === PageType.REPORT) {
            dispatchDocument({
              type: 'SET_CONFIG',
              payload: reportDocumentFromPage(action.payload),
            });
          }
          break;
        }
      }
    },
    [dispatchDocument]
  );

  const config = editMode ? draftConfig : originalConfig;

  const documentHasContent = config.pages.some(
    (page) => page.blocks.length > 0
  );
  const hasUnsavedChanges =
    draftConfig !== savedDraft ||
    (isUnsavedDraft && !hasBeenSaved && documentHasContent);

  const discardChanges = useCallback(() => {
    resetDraft(savedDraft);
  }, [resetDraft, savedDraft]);

  const saveChanges = async (): Promise<boolean> => {
    try {
      const documentToSave = reconcileReportDocument(draftConfig, 'save');
      const saved = persisted
        ? await updateReportDocument.mutateAsync({
            id: documentToSave.id,
            data: documentToSave,
          })
        : await createReportDocument.mutateAsync(documentToSave);
      setOriginalConfig(saved ?? documentToSave);
      setPersisted(true);
      if (documentToSave !== draftConfig) resetDraft(documentToSave);
      setSavedDraft(documentToSave);
      setHasBeenSaved(true);
      return new Promise<boolean>((resolve) => {
        setTimeout(() => resolve(true), 0);
      });
    } catch (error) {
      console.error('[ReportDocumentProvider] Save failed', error);
      toast({
        title: 'Save failed',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return false;
    }
  };

  const items = useMemo(() => pageItems(config.pages), [config.pages]);
  const pageConfig = useMemo(
    () => pageFromReportDocument(config, items),
    [config, items]
  );
  const tabContentView = useMemo(() => reportTabContentView(config), [config]);
  const tabDispatch = useCallback(
    (action: TabContentAction) => {
      dispatch({
        type: 'APPLY_TAB_CONTENT_ACTION',
        payload: { tabId: tabContentView.id, action },
        ...(action.replacePreviousHistorySnapshot
          ? { replacePreviousHistorySnapshot: true }
          : {}),
      });
    },
    [dispatch, tabContentView.id]
  );
  const tabContentValue = useMemo(
    () => ({ config: tabContentView, items, dispatch: tabDispatch }),
    [tabContentView, items, tabDispatch]
  );
  const flow = useMemo(
    () =>
      buildReportFlowDocument({
        title: config.title || config.name,
        description: config.description,
        created: config.created,
        pages: config.pages,
      }),
    [config]
  );
  const documentContext = useMemo(
    () => ({ document: config, flow, dispatch: dispatchDocument }),
    [config, flow, dispatchDocument]
  );

  const value: PageContextType = {
    config: pageConfig,
    dispatch,
    saveChanges,
    discardChanges,
    hasUnsavedChanges,
    undo,
    redo,
    canUndo,
    canRedo,
  };

  return (
    <ReportDocumentContext.Provider value={documentContext}>
      <PageConfigProviderFromValue value={value}>
        <TabContentProviderFromValue value={tabContentValue}>
          {children({ tabContentId: tabContentView.id })}
        </TabContentProviderFromValue>
      </PageConfigProviderFromValue>
    </ReportDocumentContext.Provider>
  );
}
